import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ArrowRight, LucideIconData } from 'lucide-angular';
import { Product, AddToCartEvent } from '../../../../shared/models/product.model';
import { ProductCardComponent } from '../../../../shared/ui/product-card/product-card.component';
import { IconComponent } from '../../../../shared/ui/icon/icon.component';
import { PublicCategory } from '../../data/home-blocks.model';
import { resolveHomeCategoryCoverUrl } from '../../../../shared/utils/home-blocks.utils';

export interface FeaturedCategoryBlockData {
  mode: 'manual' | 'newest' | 'popular';
  category: PublicCategory;
  // Productos ya mapeados a `Product` UI por el padre (HomeComponent).
  products: Product[];
}

// El doc del API recomienda 6 productos para este bloque. El backend ya envía
// el limit configurado por el admin, pero recortamos defensivamente por si
// llega más de lo esperado y rompe la cuadrícula 3×2 del layout.
const FEATURED_PRODUCT_LIMIT = 6;

/**
 * Bloque del home asociado al type `featured-category` del API. Layout dos
 * columnas (3fr / 2fr): grid 3×2 de productos a la izquierda + banner grande
 * de la categoría a la derecha. El `cover` de la categoría alimenta el banner;
 * el título y el link "Ver todos" usan `category.name` / `category.slug`.
 *
 * El `mode` (manual/newest/popular) lo decide el admin y el backend ya
 * refleja la decisión en el orden de `products` — el front no necesita
 * lógica per-mode.
 */
@Component({
  selector: 'app-featured-category-block',
  imports: [ProductCardComponent, IconComponent, RouterLink],
  templateUrl: './featured-category-block.component.html',
  styleUrl: './featured-category-block.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeaturedCategoryBlockComponent {
  readonly data = input<FeaturedCategoryBlockData | null>(null);

  protected readonly arrowRight: LucideIconData = ArrowRight;

  protected readonly title = computed(() => this.data()?.category.name ?? '');
  protected readonly categoryLink = computed(() => {
    const slug = this.data()?.category.slug;
    return slug ? `/tienda/${slug}` : '/tienda';
  });
  protected readonly bannerImage = computed(() =>
    resolveHomeCategoryCoverUrl(this.data()?.category.cover ?? null),
  );
  protected readonly bannerAlt = computed(() => {
    const name = this.title();
    return name ? `${name} — categoría destacada en Perrón Store` : '';
  });

  protected readonly products = computed<Product[]>(() => {
    const list = this.data()?.products ?? [];
    return list.slice(0, FEATURED_PRODUCT_LIMIT);
  });

  protected readonly shouldRender = computed(() => this.products().length > 0);

  addToCart = output<AddToCartEvent>();
  zoomOpen = output<Product>();
  notifyRequest = output<Product>();
  notifyCancel = output<Product>();
  cardClick = output<Product>();
  reserveClick = output<Product>();
}
